import { Button, Space, Typography } from 'antd'
import TextArea from 'antd/es/input/TextArea'
import React, { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import styled from 'styled-components'

const { Title } = Typography

const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
`

const MarkdownPreview = styled.div`
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  padding: 12px;
  min-height: 100px;
  background-color: #fafafa;
  overflow-wrap: break-word;
  color: #000000;

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-top: 16px;
    margin-bottom: 8px;
    font-weight: 600;
    color: #000000;
  }

  h1 {
    font-size: 24px;
  }
  h2 {
    font-size: 20px;
  }
  h3 {
    font-size: 18px;
  }
  h4 {
    font-size: 16px;
  }
  h5 {
    font-size: 14px;
  }
  h6 {
    font-size: 12px;
  }

  p {
    margin-bottom: 8px;
    line-height: 1.6;
    color: #000000;
  }

  ul,
  ol {
    margin-left: 24px;
    margin-bottom: 8px;
    color: #000000;
  }

  li {
    margin-bottom: 4px;
    color: #000000;
  }

  code {
    background-color: #f0f0f0;
    padding: 2px 6px;
    border-radius: 3px;
    font-family: 'Courier New', monospace;
    font-size: 0.9em;
    color: #000000;
  }

  strong {
    font-weight: 600;
    color: #000000;
  }

  em {
    font-style: italic;
    color: #000000;
  }

  blockquote {
    border-left: 4px solid #d9d9d9;
    margin-left: 0;
    padding-left: 16px;
    color: #666;
  }
`

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`

const HelpText = styled.div`
  font-size: 12px;
  color: #8c8c8c;
  margin-top: 4px;
`

interface MarkdownFieldProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  label?: string
  rows?: number
  showHelp?: boolean
}

export const MarkdownField: React.FC<MarkdownFieldProps> = ({
  value,
  onChange,
  placeholder = 'Enter markdown text...',
  label,
  rows = 4,
  showHelp = true,
}) => {
  const [isPreviewMode, setIsPreviewMode] = useState(false)

  const handleToggleMode = () => {
    setIsPreviewMode(!isPreviewMode)
  }

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value)
  }

  return (
    <FieldContainer>
      <HeaderRow>
        {label && (
          <Title level={5} style={{ margin: 0 }}>
            {label}
          </Title>
        )}
        <Space>
          <Button
            size='small'
            type={isPreviewMode ? 'default' : 'primary'}
            onClick={handleToggleMode}>
            {isPreviewMode ? 'Edit' : 'Preview'}
          </Button>
        </Space>
      </HeaderRow>

      {isPreviewMode ? (
        <MarkdownPreview>
          {value ? (
            <ReactMarkdown>{value}</ReactMarkdown>
          ) : (
            <span style={{ color: '#bfbfbf', fontStyle: 'italic' }}>
              No content to preview
            </span>
          )}
        </MarkdownPreview>
      ) : (
        <TextArea
          value={value}
          onChange={handleTextChange}
          placeholder={placeholder}
          rows={rows}
          style={{ fontFamily: 'monospace' }}
        />
      )}

      {showHelp && !isPreviewMode && (
        <HelpText>
          Supports markdown: **bold**, *italic*, # headers, - lists, `code`
        </HelpText>
      )}
    </FieldContainer>
  )
}
